import { isDefined } from "@omni/utils/helper.utils";
import { createContext, Dispatch, ReactNode, SetStateAction, use } from "react";

import { useDepth } from "@/context/depth.context";
import { useMapped } from "@/context/mapped.context";
import { FlyoutCategory } from "@/redux/modules/value/flyoutCategories/flyoutCategories.types";

type SidebarContextValue = {
    accountLinks: Array<{
        label: string;
        link: string;
    }>;
    profileLink: string;
    innerHtml?: string;
    activeCategoryIndexes: Array<number>;
    categories: FlyoutCategory[];
    assortimentOpen: boolean;
    setAssortimentOpen: Dispatch<SetStateAction<boolean>>;
    setActiveCatetoryIndexes: Dispatch<SetStateAction<Array<number>>>;
};

type SidebarContextProviderProps = {
    accountLinks: Array<{
        label: string;
        link: string;
    }>;
    profileLink: string;
    innerHtml?: string;
    rootCategory: FlyoutCategory;
    activeCategoryIndexes: Array<number>;
    assortimentOpen: boolean;
    setAssortimentOpen: Dispatch<SetStateAction<boolean>>;
    setActiveCatetoryIndexes: Dispatch<SetStateAction<Array<number>>>;
    children: ReactNode;
};

export const SidebarContext = createContext<SidebarContextValue | undefined>(
    undefined
);
SidebarContext.displayName = "SidebarContext";

export const SidebarContextProvider = ({
    accountLinks,
    profileLink,
    innerHtml,
    rootCategory,
    activeCategoryIndexes,
    assortimentOpen,
    setAssortimentOpen,
    setActiveCatetoryIndexes,
    children
}: SidebarContextProviderProps) => {
    const categories = activeCategoryIndexes.reduce(
        (acc, curr) => {
            const subCategory = acc.at(-1)?.subCategories?.[curr];
            if (!subCategory) return acc;

            acc.push(subCategory);

            return acc;
        },
        [rootCategory]
    );

    return (
        <SidebarContext
            value={{
                accountLinks: accountLinks,
                profileLink: profileLink,
                innerHtml: innerHtml,
                activeCategoryIndexes: activeCategoryIndexes,
                categories: categories,
                assortimentOpen: assortimentOpen,
                setAssortimentOpen: setAssortimentOpen,
                setActiveCatetoryIndexes: setActiveCatetoryIndexes
            }}
        >
            {children}
        </SidebarContext>
    );
};

export const useSidebarContext = () => {
    const context = use<SidebarContextValue | undefined>(SidebarContext);

    if (!isDefined(context)) {
        throw new Error(`${SidebarContext.displayName} is undefined.`);
    }

    return context;
};

export const useCategory = () => {
    const { categories } = useSidebarContext();
    const { index } = useMapped(0);
    const { depth } = useDepth();

    return { category: categories?.at(index + depth) };
};

export const useSubCategory = () => {
    const { categories } = useSidebarContext();
    const { index } = useMapped();
    const { depth } = useDepth();

    return { subCategory: categories?.at(depth)?.subCategories?.at(index) };
};
